import { Link, createFileRoute } from '@tanstack/react-router'
import {
  ArrowRight,
  BookOpen,
  Globe,
  GraduationCap,
  HardHat,
} from 'lucide-react'
import { Button, LinkButton } from '@react-spectrum/s2'
import ChevronDown from '@react-spectrum/s2/icons/ChevronDown'
import {
  CHAT_QUICK_PROMPTS,
  pickRandomQuickPrompts,
} from '@shared/chat-quick-prompts'
import { trackAnalyticsEvent } from '@client/analytics'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { HomeChatEntry } from '@/components/HomeChatEntry'
import devolvedNations480 from '@/assets/landing/devolved-nations-480.webp'
import devolvedNations800 from '@/assets/landing/devolved-nations-800.webp'
import healthSafety480 from '@/assets/landing/health-safety-480.webp'
import healthSafety800 from '@/assets/landing/health-safety-800.webp'
import industryStandards480 from '@/assets/landing/industry-standards-480.webp'
import industryStandards800 from '@/assets/landing/industry-standards-800.webp'
import sectorGuidance480 from '@/assets/landing/sector-guidance-480.webp'
import sectorGuidance800 from '@/assets/landing/sector-guidance-800.webp'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/')({
  loader: () => ({
    initialQuickPrompts: pickRandomQuickPrompts(CHAT_QUICK_PROMPTS, 3),
  }),
  component: LandingPage,
  head: () => ({
    meta: [
      {
        title:
          'Building regs assistant | AI for UK compliance & approved documents',
      },
      {
        name: 'description',
        content:
          'The professional AI assistant for UK Building Regulations. Search Approved Documents (Part A-T), Scottish Technical Handbooks, and British Standards instantly.',
      },
      {
        name: 'keywords',
        content:
          'building regulations, approved documents, part b fire safety, scottish technical handbooks, cdm regulations, ai construction, architecture compliance',
      },
      {
        property: 'og:title',
        content: 'Building regs assistant | AI for UK compliance',
      },
      {
        property: 'og:description',
        content:
          'Instant, cited answers from the official UK Building Regulations library. For architects, surveyors, and developers.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: '/images/pricing.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Building regs assistant',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'Offer',
            price: '49.00',
            priceCurrency: 'GBP',
          },
          author: {
            '@type': 'Organization',
            name: 'Tidy Technology Ltd',
            url: 'https://www.buildingregs.app', // Replace with actual domain
          },
        }),
      },
    ],
  }),
})

function LandingPage() {
  const { initialQuickPrompts } = Route.useLoaderData()
  const standards = [
    {
      title: 'Devolved nations',
      subtitle: 'Beyond England',
      description:
        'We cover the distinct regulations for Scotland (Technical Handbooks), Wales (Approved Documents), and Northern Ireland (Technical Booklets). Essential for UK-wide projects.',
      icon: <Globe className="w-8 h-8 text-primary" />,
      image: {
        src: devolvedNations800,
        srcSet: `${devolvedNations480} 480w, ${devolvedNations800} 800w`,
        width: 800,
        height: 533,
      },
    },
    {
      title: 'Health & safety',
      subtitle: 'Statutory compliance',
      description:
        'Navigate the CDM Regulations 2015 and HSE Guidance (HSG168, GS6) to manage risk and avoid criminal liability on site.',
      icon: <HardHat className="w-8 h-8 text-primary" />,
      image: {
        src: healthSafety800,
        srcSet: `${healthSafety480} 480w, ${healthSafety800} 800w`,
        width: 800,
        height: 533,
      },
    },
    {
      title: 'Sector guidance',
      subtitle: 'Specialized codes',
      description:
        'Specific standards for Schools (Building Bulletins like BB103, BB93) and Healthcare (HTMs and HBNs) for specialized project requirements.',
      icon: <GraduationCap className="h-8 w-8 text-primary" />,
      image: {
        src: sectorGuidance800,
        srcSet: `${sectorGuidance480} 480w, ${sectorGuidance800} 800w`,
        width: 800,
        height: 1200,
      },
    },
    {
      title: 'Industry standards',
      subtitle: 'The de facto rules',
      description:
        'Access critical standards like BS 7671 (Wiring), BS 8300 (Accessibility), NHBC Standards for housebuilders, and BBA Certificates for product compliance.',
      icon: <BookOpen className="h-8 w-8 text-primary" />,
      image: {
        src: industryStandards800,
        srcSet: `${industryStandards480} 480w, ${industryStandards800} 800w`,
        width: 800,
        height: 450,
      },
    },
  ]

  const faqs = [
    {
      q: 'Do you cover Scottish Technical Handbooks?',
      a: 'Yes. Unlike many tools that focus only on English Approved Documents, we index the distinct Technical Handbooks for Scotland (Domestic and Non-Domestic), which are critical for projects north of the border.',
    },
    {
      q: 'Can I find planning policy guidance?',
      a: 'Absolutely. We include the National Planning Policy Framework (NPPF), National Design Guide, and the London Plan to help you at the pre-planning stage.',
    },
    {
      q: 'What about sustainability standards?',
      a: 'We cover optional but high-value standards like BREEAM Technical Standards and Passivhaus Criteria for your eco-conscious and commercial projects.',
    },
  ]

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground font-sans selection:bg-primary/30">
      <Header />
      <main id="main-content" className="flex-1">
        {/* Hero Section */}
        <section className="relative px-6 pb-20 pt-16 text-center md:pt-32">
          <div className="container mx-auto max-w-4xl relative z-10">
            <h1 className="mb-8 text-5xl font-bold tracking-tight leading-[1.1] md:text-7xl">
              Trusted answers.
              <br />
              Confident decisions.
            </h1>
            <p className="mx-auto mb-12 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              From Approved Documents to critical sectoral standards and
              planning policy frameworks, BuildingRegs.app is the only AI
              assistant that covers the full spectrum of UK building and
              construction compliance.
            </p>
            <div className="flex w-full flex-col items-center justify-center gap-4">
              <HomeChatEntry initialQuickPrompts={initialQuickPrompts} />
            </div>
          </div>
        </section>

        {/* Feature Blocks */}
        <section id="library" className="py-12 px-6 scroll-mt-24">
          <div className="container mx-auto max-w-4xl space-y-12">
            <h2 className="sr-only">
              UK construction standards and guidance covered
            </h2>
            {standards.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col overflow-hidden rounded-[1.75rem] border border-border/80 bg-card shadow-sm md:flex-row"
              >
                <div className="md:w-2/5 h-64 md:h-auto relative overflow-hidden group">
                  <img
                    src={feature.image.src}
                    srcSet={feature.image.srcSet}
                    sizes="(min-width: 768px) 358px, calc(100vw - 3rem)"
                    alt={feature.title}
                    width={feature.image.width}
                    height={feature.image.height}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-foreground/10 mix-blend-multiply"></div>
                </div>
                <div className="md:w-3/5 p-10 md:p-16 flex flex-col justify-center space-y-6">
                  <div className="flex items-center gap-3 mb-2">
                    {feature.icon}
                    <span className="text-xs font-medium text-muted-foreground">
                      {feature.subtitle}
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold tracking-tight text-foreground">
                    {feature.title}
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                  <Link
                    to="/chat"
                    onClick={() =>
                      trackAnalyticsEvent('landing_feature_chat_clicked', {
                        feature_index: index,
                      })
                    }
                    className="inline-flex items-center text-sm font-medium text-primary transition-colors hover:text-primary/80"
                  >
                    Search these docs{' '}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="py-16 px-6 text-center">
          <div className="container mx-auto max-w-4xl">
            <Link
              to="/pricing"
              onClick={() =>
                trackAnalyticsEvent('pricing_cta_clicked', {
                  location: 'landing_page',
                })
              }
              className="inline-flex items-center justify-center gap-2 text-sm font-medium text-primary transition-colors hover:text-primary/80"
            >
              View pricing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="border-t border-border/70 px-6 py-24">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="text-4xl font-bold tracking-tight mb-6">
              Frequently asked questions
            </h2>
            <p className="mx-auto mb-12 max-w-2xl text-sm text-muted-foreground">
              Everything you need to know about our coverage and compliance.
            </p>

            <div className="mb-16">
              <button
                type="button"
                onClick={() => {
                  trackAnalyticsEvent('landing_faq_jump_clicked')
                  document
                    .getElementById('faq-content')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className="group inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span>Learn more</span>
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
              </button>
            </div>

            <div
              id="faq-content"
              className="space-y-12 rounded-[1.75rem] border border-border/80 bg-card p-10 text-left shadow-sm md:p-16"
            >
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={cn(
                    'space-y-4',
                    index !== 0 && 'border-t border-border/70 pt-12',
                  )}
                >
                  <h3 className="text-xl font-bold text-foreground">{faq.q}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {faq.a}
                  </p>
                  <Link
                    to="/chat"
                    onClick={() =>
                      trackAnalyticsEvent('landing_faq_chat_clicked', {
                        faq_index: index,
                      })
                    }
                    className="text-xs font-medium text-primary hover:text-primary/80"
                  >
                    Try asking about this...
                  </Link>
                </div>
              ))}

              <div className="pt-8 text-center">
                <LinkButton
                  href="/chat"
                  size="L"
                  onPress={() =>
                    trackAnalyticsEvent('landing_start_using_ai_clicked')
                  }
                >
                  Start using AI
                </LinkButton>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
